import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authApi, orderApi } from '../lib/api';
import { cartTotalCents, useCart } from '../lib/cart';

interface Slot { iso: string; label: string }

export function Checkout() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [cart, cartActions] = useCart();
  const [user, setUser] = useState<{ id: string; email: string; role: string } | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [pickedSlot, setPickedSlot] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [email, setEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [sendingMagic, setSendingMagic] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = useMemo(() => cartTotalCents(cart), [cart]);

  useEffect(() => {
    authApi.me().then((res) => {
      if (res?.ok) {
        setUser(res.user);
        setEmail(res.user.email);
      }
    });
  }, []);

  useEffect(() => {
    if (!slug) return;
    orderApi
      .getSlots(slug)
      .then((res) => setSlots(res.data))
      .catch((err) => setError((err as Error).message));
  }, [slug]);

  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email.match(/^[^@]+@[^@]+\.[^@]+$/)) {
      setError('Email invalide.');
      return;
    }
    setSendingMagic(true);
    setError(null);
    try {
      await authApi.magicLink(email);
      setMagicSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSendingMagic(false);
    }
  }

  async function placeOrder() {
    if (!cart || !slug || !pickedSlot) return;
    setPlacingOrder(true);
    setError(null);
    try {
      const res = await orderApi.create({
        shop_slug: slug,
        pickup_slot: pickedSlot,
        items: cart.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        notes: notes || undefined,
        buyer_phone: phone || undefined,
      });
      cartActions.clear();
      navigate(`/mes-commandes/${res.data.id}`);
    } catch (err) {
      const code = (err as Error).message;
      setError(
        code === 'insufficient_stock'
          ? 'Stock insuffisant pour un des produits. Actualisez la page.'
          : code === 'invalid_slot'
            ? 'Ce créneau n’est plus disponible. Choisissez-en un autre.'
            : code === 'shop_unavailable'
              ? 'La boutique n’est plus disponible.'
              : `Commande impossible (${code}).`,
      );
    } finally {
      setPlacingOrder(false);
    }
  }

  if (!cart || cart.items.length === 0 || cart.shop_slug !== slug) {
    return (
      <main className="container">
        <h2>Panier vide</h2>
        <p className="muted">Ajoutez des produits depuis une boutique pour commander.</p>
      </main>
    );
  }

  return (
    <main className="container">
      <h2>Finaliser la commande</h2>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Retrait chez {cart.shop_name}</h3>
        {slots.length === 0 ? (
          <p className="muted">Aucun créneau disponible pour les 7 prochains jours. Revenez plus tard.</p>
        ) : (
          <>
            <p className="muted" style={{ margin: 0 }}>Choisissez un créneau de retrait en magasin :</p>
            <div className="slot-grid">
              {slots.slice(0, 60).map((s) => (
                <button
                  key={s.iso}
                  type="button"
                  className={pickedSlot === s.iso ? 'active' : ''}
                  onClick={() => setPickedSlot(s.iso)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {slots.length > 60 && (
              <p className="muted" style={{ marginTop: 8 }}>
                {slots.length - 60} créneaux supplémentaires non affichés.
              </p>
            )}
          </>
        )}
      </div>

      {!user ? (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Vos coordonnées</h3>
          {!magicSent ? (
            <form onSubmit={sendMagicLink}>
              <p className="muted">Nous vous envoyons un lien de connexion par email — pas de mot de passe à retenir.</p>
              <div className="form-row">
                <label>Email</label>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button className="primary" type="submit" disabled={sendingMagic}>
                {sendingMagic ? 'Envoi…' : 'Recevoir le lien de connexion'}
              </button>
            </form>
          ) : (
            <div className="alert alert-info">
              Un email vous a été envoyé à <strong>{email}</strong>. Cliquez sur le lien puis revenez sur cette page pour finaliser la commande.
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Vos coordonnées</h3>
          <p className="muted">Connecté en tant que <strong>{user.email}</strong>.</p>
          <div className="form-row">
            <label>Téléphone (pour recevoir un SMS quand votre commande est prête)</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07 12 34 56 78" />
          </div>
          <div className="form-row">
            <label>Note pour le commerçant (optionnel)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Récapitulatif</h3>
        <table>
          <tbody>
            {cart.items.map((i) => (
              <tr key={i.product_id}>
                <td>{i.name}</td>
                <td style={{ textAlign: 'right', width: 60 }}>× {i.quantity}</td>
                <td style={{ textAlign: 'right', width: 100 }}>{((i.price_cents * i.quantity) / 100).toFixed(2)} €</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total à régler au retrait</strong></td>
              <td></td>
              <td style={{ textAlign: 'right' }}><strong>{(total / 100).toFixed(2)} €</strong></td>
            </tr>
          </tfoot>
        </table>
        <p className="muted" style={{ marginTop: 12 }}>
          Pas de paiement en ligne : vous réglez directement au commerçant lors du retrait.
        </p>
      </div>

      <button
        className="primary"
        style={{ width: '100%', padding: '14px' }}
        disabled={!user || !pickedSlot || placingOrder}
        onClick={placeOrder}
      >
        {placingOrder ? 'Envoi de la commande…' : 'Confirmer ma commande'}
      </button>
      {!user && <p className="muted" style={{ textAlign: 'center', marginTop: 8 }}>Connectez-vous pour confirmer votre commande.</p>}
      {!pickedSlot && user && <p className="muted" style={{ textAlign: 'center', marginTop: 8 }}>Choisissez un créneau de retrait.</p>}
    </main>
  );
}
